/**
 * Vacation mode — one switch that stops time-based nagging while you are away.
 *
 * iter364. The training plan itself never needed pausing: weeks advance only
 * when a workout is logged, so the plan already waits. What kept running on
 * holiday was everything measured in DAYS — reminders, the streak, the weekly
 * email scoring untracked days, and above all the Mini-Cut clock, which is
 * counted from its start date and therefore burned real cut days while the
 * user was on a beach.
 *
 * Dates are plain 'YYYY-MM-DD' and are compared as STRINGS or parsed as LOCAL
 * midnight — never through `new Date('YYYY-MM-DD')`, which the JS spec treats
 * as UTC midnight (that bug already cost us the nutrition date arrows, iter362).
 */

export type FoodTrackingMode = 'log' | 'off';
export type VacationEmailMode = 'full' | 'none';

export interface VacationSettings {
  startDate: string;
  endDate: string;
  /** true = "make a break" (cut clock pauses), false = "let it go" (clock runs on) */
  pauseCut: boolean;
  foodTracking: FoodTrackingMode;
  weeklyEmail: VacationEmailMode;
  updatedAt?: string;
}

export const VACATION_STORAGE_KEY = 'gym_notebook_vacation';
export const VACATION_CHANGED_EVENT = 'vacationChanged';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const todayLocal = (): string => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

/** 'YYYY-MM-DD' → local midnight Date. */
export const parseLocalDate = (value: string): Date => {
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const formatLocalDate = (d: Date): string => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

export function normalizeVacation(raw: any): VacationSettings | null {
  if (!raw || typeof raw !== 'object') return null;
  const start = String(raw.startDate || '').slice(0, 10);
  const end = String(raw.endDate || '').slice(0, 10);
  if (!DATE_RE.test(start) || !DATE_RE.test(end) || end < start) return null;
  return {
    startDate: start,
    endDate: end,
    pauseCut: raw.pauseCut !== false,
    foodTracking: raw.foodTracking === 'off' ? 'off' : 'log',
    weeklyEmail: raw.weeklyEmail === 'none' ? 'none' : 'full',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : undefined,
  };
}

export function loadVacation(): VacationSettings | null {
  try {
    const raw = localStorage.getItem(VACATION_STORAGE_KEY);
    return raw ? normalizeVacation(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/** Writes to the server first and only then locally, so a rejected save can't
 *  leave this device showing a trip the server never accepted (QA iter355).
 *  An offline edit is queued and replayed when the connection returns. */
export async function saveVacation(settings: VacationSettings | null): Promise<void> {
  const normalized = settings ? normalizeVacation(settings) : null;
  if (settings && !normalized) throw new Error('Those dates are not a valid range');

  const { getToken } = await import('./tokenStorage');
  const { sendOrQueue } = await import('./offlineQueue');
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const stamped = normalized ? { ...normalized, updatedAt: new Date().toISOString() } : null;
  const res = await sendOrQueue(
    '/api/data/user-bodyweight',
    {
      method: 'PUT',
      headers,
      credentials: 'include' as RequestCredentials,
      body: JSON.stringify({ vacation: stamped }),
    },
    { label: 'Vacation mode', dedupeKey: 'vacation' },
  );
  // `null` means the write was queued for later — treat it as accepted, that is
  // the whole point of the queue.
  if (res && !res.ok) {
    throw new Error(
      res.status === 401
        ? 'Please sign in again to save your vacation'
        : 'Could not save your vacation — please try again',
    );
  }

  try {
    if (stamped) {
      localStorage.setItem(VACATION_STORAGE_KEY, JSON.stringify(stamped));
    } else {
      localStorage.removeItem(VACATION_STORAGE_KEY);
    }
  } catch { /* quota */ }
  try {
    window.dispatchEvent(new Event(VACATION_CHANGED_EVENT));
  } catch { /* SSR */ }
}

/** Hydrate from a `/api/data/user-bodyweight` payload.
 *
 *  A local copy that is NEWER than the server's is an edit made offline (or one
 *  whose PUT is still queued), so it wins and gets pushed up instead of being
 *  silently overwritten — the same latest-wins rule the weight goal uses. */
export function applyVacationFromServer(data: any): void {
  if (!data || !('vacation' in data)) return;
  const incoming = normalizeVacation(data.vacation);
  const local = loadVacation();
  const localAt = Date.parse(local?.updatedAt || '') || 0;
  const serverAt = Date.parse(incoming?.updatedAt || '') || 0;
  if (localAt && localAt > serverAt) {
    // Our copy is newer. Re-send it rather than accept the stale one.
    saveVacation(local).catch(() => { /* queued / will retry */ });
    return;
  }
  try {
    if (incoming) {
      localStorage.setItem(VACATION_STORAGE_KEY, JSON.stringify(incoming));
    } else {
      localStorage.removeItem(VACATION_STORAGE_KEY);
    }
    window.dispatchEvent(new Event(VACATION_CHANGED_EVENT));
  } catch { /* quota / SSR */ }
}

export function isVacationDay(dateStr: string, vacation?: VacationSettings | null): boolean {
  const v = vacation === undefined ? loadVacation() : vacation;
  if (!v) return false;
  const day = String(dateStr).slice(0, 10);
  return day >= v.startDate && day <= v.endDate;
}

/** Inclusive count of vacation days inside [fromStr, toStr]. */
export function vacationDaysBetween(
  fromStr: string,
  toStr: string,
  vacation?: VacationSettings | null,
): number {
  const v = vacation === undefined ? loadVacation() : vacation;
  if (!v) return 0;
  const from = String(fromStr).slice(0, 10);
  const to = String(toStr).slice(0, 10);
  if (to < from) return 0;
  const start = from > v.startDate ? from : v.startDate;
  const end = to < v.endDate ? to : v.endDate;
  if (end < start) return 0;
  const days = Math.round(
    (parseLocalDate(end).getTime() - parseLocalDate(start).getTime()) / 86400000,
  );
  return days + 1;
}

/** Days the cut clock should NOT have counted between the block start and a date. */
export function pausedCutDays(
  goalStart: string | null | undefined,
  dateStr: string,
  vacation?: VacationSettings | null,
): number {
  const v = vacation === undefined ? loadVacation() : vacation;
  if (!v || !v.pauseCut || !goalStart) return 0;
  return vacationDaysBetween(String(goalStart).slice(0, 10), dateStr, v);
}

/** True when every day of the Mon-start week is inside the vacation. */
export function isWeekFullyVacation(
  weekStart: Date | number,
  vacation?: VacationSettings | null,
): boolean {
  const v = vacation === undefined ? loadVacation() : vacation;
  if (!v) return false;
  const start = typeof weekStart === 'number' ? new Date(weekStart) : new Date(weekStart);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    if (!isVacationDay(formatLocalDate(d), v)) return false;
  }
  return true;
}

export type VacationPhase = 'active' | 'upcoming' | 'past';

export function vacationPhase(
  vacation?: VacationSettings | null,
  today: string = todayLocal(),
): VacationPhase | null {
  const v = vacation === undefined ? loadVacation() : vacation;
  if (!v) return null;
  if (today < v.startDate) return 'upcoming';
  if (today > v.endDate) return 'past';
  return 'active';
}

export function vacationDayCount(vacation: VacationSettings): number {
  return (
    Math.round(
      (parseLocalDate(vacation.endDate).getTime() - parseLocalDate(vacation.startDate).getTime()) /
        86400000,
    ) + 1
  );
}

/** "12 Jun – 24 Jun" — short, human, no year noise unless it crosses one. */
export function formatVacationRange(vacation: VacationSettings): string {
  const s = parseLocalDate(vacation.startDate);
  const e = parseLocalDate(vacation.endDate);
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const sameYear = s.getFullYear() === e.getFullYear();
  const left = s.toLocaleDateString(undefined, sameYear ? opts : { ...opts, year: 'numeric' });
  const right = e.toLocaleDateString(undefined, sameYear ? opts : { ...opts, year: 'numeric' });
  return `${left} – ${right}`;
}
